import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import { FunctionComponent, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { MIN_TRANSACTIONS, MAX_TRANSACTIONS } from "../../../utils/constants";

interface TransactionNumberModalProps {
    show: boolean;
    onClose: (number: number) => void;
    onSave?: (data: {
        name: string;
        category: string;
        transactionType: string;
        serviceProvider: string;
        serviceRecipient: string;
        method: string;
        testedParty: string;
        benchmarkAnalysis: string;
    }) => void;
}

type FormFields = {
    number: number;
};

export const TransactionNumberModal: FunctionComponent<TransactionNumberModalProps> = ({ show, onClose }) => {
    const [selectedNumber, setSelectedNumber] = useState(1);

    const {
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<FormFields>({
        values: { number: selectedNumber },
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<FormFields> = async () => {
        onClose(selectedNumber);
    };

    const closeNumberModal = () => {
        onClose(0);
    };

    const handleQuickSelect = (num: number) => {
        setSelectedNumber(num);
        setValue("number", num, { shouldValidate: true });
    };

    const handleNumberInputChange = (valueString: string, valueAsNumber: number) => {
        if (!isNaN(valueAsNumber) && valueAsNumber >= MIN_TRANSACTIONS && valueAsNumber <= MAX_TRANSACTIONS) {
            setSelectedNumber(valueAsNumber);
        }
    };

    const quickSelectNumbers = [1, 2, 3, 5, 10];

    return (
        <Modal isOpen={show} onClose={closeNumberModal} size="md">
            <ModalOverlay />
            <ModalContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader>
                        <Text fontSize="lg" fontWeight="semibold">
                            Add Transactions
                        </Text>
                        <Text fontSize="sm" fontWeight="normal" mt={1}>
                            How many transactions would you like to create?
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody>
                        <VStack spacing={5} align="stretch">
                            {/* Quick Select */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" mb={3}>
                                    Quick Select
                                </Text>
                                <SimpleGrid columns={5} spacing={2}>
                                    {quickSelectNumbers.map(num => (
                                        <Button
                                            key={num}
                                            size="md"
                                            variant={selectedNumber === num ? "solid" : "outline"}
                                            colorScheme={selectedNumber === num ? "blue" : "gray"}
                                            onClick={() => handleQuickSelect(num)}
                                            h="40px"
                                        >
                                            {num}
                                        </Button>
                                    ))}
                                </SimpleGrid>
                            </Box>

                            {/* Custom Input */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" mb={3}>
                                    Or enter custom amount
                                </Text>
                                <FormControl isInvalid={!!errors?.number}>
                                    <NumberInput
                                        value={selectedNumber}
                                        onChange={handleNumberInputChange}
                                        min={MIN_TRANSACTIONS}
                                        max={MAX_TRANSACTIONS}
                                        step={1}
                                        size="md"
                                    >
                                        <NumberInputField textAlign="center" placeholder={`Enter number (${MIN_TRANSACTIONS}-${MAX_TRANSACTIONS})`} />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>

                                    {errors?.number && <FormErrorMessage mt={2}>{errors.number.message}</FormErrorMessage>}
                                </FormControl>
                            </Box>

                            {/* Preview */}
                            <Box textAlign="center" py={2}>
                                <Text fontSize="sm">
                                    Creating {selectedNumber} {selectedNumber === 1 ? "transaction" : "transactions"}
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={closeNumberModal}>
                            Cancel
                        </Button>
                        <Button type="submit" colorScheme="blue" isDisabled={selectedNumber < MIN_TRANSACTIONS || selectedNumber > MAX_TRANSACTIONS}>
                            Create {selectedNumber === 1 ? "Transaction" : "Transactions"}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
